import { FC, ReactNode } from "react";
import { Alert, Typography, Space, Card, Row, Col, Anchor, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DollarOutlined, LogoutOutlined, SafetyOutlined } from "@ant-design/icons";
import { PublicPageLayout } from "../../components/public/PublicPageLayout";
import { SEOHead } from "../../components/SEOHead";
import {
  PUBLIC_PLAN_ORDER,
  formatPlanMonthlyPrice,
  getSubscriptionPlanConfig,
  formatPlanTaxIncludedLabel,
} from "@shared/config/subscription-plans";

const { Title, Paragraph, Text } = Typography;

type Section = {
  heading: string;
  body: JSX.Element;
};

type PlanTableRow = {
  key: string;
  plan: string;
  price: ReactNode;
  equipmentLimit: string;
  storageCapacity: string;
};

const fullWidthDigits = ["０", "１", "２", "３", "４", "５", "６", "７", "８", "９"];

const toFullWidthNumber = (value: number): string =>
  value
    .toString()
    .split("")
    .map((digit) => fullWidthDigits[Number(digit)])
    .join("");

const includesArticleNumber = (heading: string, articleNumber: number): boolean => {
  const ascii = `第${articleNumber}条`;
  const fullWidth = `第${toFullWidthNumber(articleNumber)}条`;
  return heading.includes(ascii) || heading.includes(fullWidth);
};

const formatPlanLimitValue = (value: number | null): string =>
  typeof value === "number" ? String(value) : "個別設定";

const formatStorageCapacity = (capacityGB: number | null): string => {
  if (capacityGB === null) return "無制限";
  if (capacityGB >= 1024) return `${capacityGB / 1024}TB`;
  return `${capacityGB}GB`;
};

const planTableColumns: ColumnsType<PlanTableRow> = [
  { title: "プラン名", dataIndex: "plan", key: "plan" },
  { title: "月額料金", dataIndex: "price", key: "price" },
  { title: "設備数上限", dataIndex: "equipmentLimit", key: "equipmentLimit" },
  { title: "ストレージ容量", dataIndex: "storageCapacity", key: "storageCapacity" },
];

const planTableData: PlanTableRow[] = PUBLIC_PLAN_ORDER.map((code) => {
  const plan = getSubscriptionPlanConfig(code);
  return {
    key: plan.code,
    plan: plan.label,
    price: (
      <span>
        <Text>{formatPlanMonthlyPrice(plan)}</Text>
        <br />
        <Text type="secondary">({formatPlanTaxIncludedLabel(plan)})</Text>
      </span>
    ),
    equipmentLimit: formatPlanLimitValue(plan.equipmentLimit),
    storageCapacity: formatStorageCapacity(plan.storageCapacityGB),
  };
});

const privacyPolicyUrl = "https://dio-meta.com/privacypolicy/";

const termsSections: Section[] = [
  {
    heading: "一元帳 SaaS利用規約（法人向け）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>
          本契約（以下「本契約」という。）は、株式会社ＤｉＯ（以下「甲」という。）が提供するクラウドサービス「一元帳」（以下「本件サービス」という。）の利用に関し、甲と本件サービスの利用を希望する法人または事業者（以下「乙」という。）との間の権利義務関係を明確にするために定めるものである。
        </Paragraph>
        <Paragraph>
          乙は、本契約の全ての内容に同意のうえで本件サービスを利用するものとする。
        </Paragraph>
      </Space>
    ),
  },
  {
    heading: "第１条（目的及びサービスの内容）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．甲は、乙に対し、インターネットを通じて設備管理・健全度予測・資産価値評価等を行うクラウドサービス「一元帳」を提供する。</Paragraph>
        <Paragraph>２．本件サービスの各種プラン及び機能は別表に定めるものとし、甲は、社会情勢、法令改正、技術的理由その他の必要がある場合、随時その内容を改定することができる。</Paragraph>
        <Paragraph>３．甲は、本件サービスの提供に付随して、有料又は無償の追加機能その他の付帯サービスを提供することがあり、その内容及び条件は別途甲が定めるものとする。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第２条（契約の成立）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．乙が甲所定の申込画面において本契約に同意のうえ、必要事項を入力して申込を行い、甲がこれを承諾した時点をもって、本契約は成立するものとする。</Paragraph>
        <Paragraph>２．乙が以下の各号のいずれかに該当する場合には、甲は申込を拒否し、契約成立後に発覚した場合には、契約を解除できるものとする。この場合、乙は甲に対し何らの異議申立て、要求ないし請求を行わない。</Paragraph>
        <ul style={{ paddingLeft: 24, margin: 0 }}>
          <li>（１）虚偽または不正確な情報を申告した場合</li>
          <li>（２）暴力団その他の反社会的勢力に該当する場合</li>
          <li>（３）過去に本契約または他の甲のサービスに関して契約違反があった場合</li>
          <li>（４）その他、甲が不適当と判断した場合</li>
        </ul>
        <Paragraph>３．乙は、甲が発行する本件サービスのアカウントＩＤ及びパスワードを厳重に保管・管理する。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第３条（利用料金及び支払方法）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．乙は、甲が定める月額料金を以下のプランに従い支払うものとする。</Paragraph>
        <Table
          columns={planTableColumns}
          dataSource={planTableData}
          pagination={false}
          size="small"
          rowKey="key"
        />
        <Paragraph>２．甲は、本件サービスに関し、料金体系、提供プラン、支払方法、又は付帯サービス等の内容を、将来にわたり新設、変更、廃止又は追加することができるものとし、その詳細は別途甲が定める。</Paragraph>
        <Paragraph>３．乙は、クレジットカード決済の方法により、当月分の利用料金を支払うものとする。</Paragraph>
        <Paragraph>４．支払済みの利用料金は、いかなる理由によっても返金されないものとする。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第４条（契約期間及び更新）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．本契約の有効期間は、利用開始日から起算して１か月間とし、期間満了の前日までに乙または甲のいずれかが更新停止の申出をしない限り、同一条件にて自動的に１か月単位で更新されるものとする。</Paragraph>
        <Paragraph>２．乙は、甲の指定する手続によってのみ自動更新の停止を申請できるものとし、申請をもって次回更新以降の契約継続を停止することができる。</Paragraph>
        <Paragraph>３．乙が契約期間の途中で更新停止を行った場合であっても、日割り計算は行わず、当該進行期間にかかる料金は返金されないものとする。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第５条（禁止事項）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．乙は、本件サービスの利用にあたり、次の各号の行為を行ってはならないものとする。</Paragraph>
        <ul style={{ paddingLeft: 24, margin: 0, listStyle: "none" }}>
          <li>①本件サービス又は甲のシステムへの不正アクセス、改ざん、リバースエンジニアリング等の行為</li>
          <li>②第三者への本契約上の地位または本契約に関する権利の譲渡、転貸、又は本件サービスのデータ・成果物の無断利用・転用</li>
          <li>③法令、公序良俗又は第三者の権利を侵害する行為</li>
          <li>④虚偽の登録又は他者になりすましての利用</li>
          <li>⑤本件サービスの運営を妨げる行為</li>
          <li>⑥甲が発行するアカウントＩＤ及びパスワードを、契約上認められた利用者以外の第三者と共有、貸与、譲渡、又は共同利用する行為</li>
          <li>⑦本件サービスのシステム等に過度な負荷をかける行為、甲のネットワークまたはシステムへの不正アクセス</li>
          <li>⑧第三者のＩＤまたはパスワードを利用する行為</li>
        </ul>
        <Paragraph>２．甲が発行したアカウントＩＤ及びパスワードにより本件サービスが利用された場合、乙による利用とみなし、これが乙または契約上認められた利用者以外の第三者による利用の場合には、乙は自己の責任と費用においてこれを解決し、甲は一切の責任を負わないものとする。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第６条（データの取扱い及び個人情報）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．乙が本件サービスを通じて登録・入力した情報及びデータ（以下「利用データ」という。）の所有権は乙に帰属する。</Paragraph>
        <Paragraph>２．甲は、安定的なサービス提供及び品質向上を目的として、利用データを統計化・非個人識別化したうえで、分析及び研究開発に利用することができるものとし、乙はこれを承諾する。</Paragraph>
        <Paragraph>３．甲は、利用契約終了後も、法令又はサービス運営上必要な範囲で利用データを保管することができるものとし、その保管期間については特に定めない。</Paragraph>
        <Paragraph>４．甲は、本契約に関して乙から開示を受けた個人情報保護法所定の個人情報を、別途定める「個人情報保護方針」に従い、適正に管理するものとする。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第７条（秘密保持）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>甲及び乙は、本契約に関連して知り得た相手方の営業上、技術上または経営上の情報を、相手方の書面またはこれに準ずる手段による承諾なく第三者に開示または漏洩してはならない。</Paragraph>
        <Paragraph>但し、法令に基づく開示命令があった場合又は相手方の事前の書面による承諾を得た場合はこの限りでない。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第８条（外部サービスとの連携）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．甲は、安定的な本件サービスの提供を目的として、Salesforce、CRM-LINKその他の外部サービスと連携を行うことができるものとする。</Paragraph>
        <Paragraph>２．甲は、外部サービスとの連携に際し、利用データを含む情報を共有する場合があることを乙は予め承諾するものとする。</Paragraph>
        <Paragraph>３．外部サービス側で発生した障害、データ漏洩、又はその他の損害について、甲は一切の責任を負わないものとする。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第９条（知的財産権）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．本件サービス及びこれに関連するプログラム、データベース、マニュアル、仕様書、設計資料その他一切の著作物、ノウハウ及び技術情報に関する知的財産権はすべて甲に帰属する。本サービスの利用許諾は、乙に対し本件サービスに関する甲のいかなる知的財産権その他の権利、ノウハウまたはその使用を許諾するものではない。</Paragraph>
        <Paragraph>２．乙は、甲の事前の書面による承諾なく、本件サービスの全部または一部を複製、改変、翻案、転載、譲渡又は貸与等してはならない。</Paragraph>
        <Paragraph>３．乙が甲の承諾を得て作成した改変、翻案、派生その他の変更後の資料またはデータの著作権も甲に帰属するものとする。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第１０条（サービスの停止及び変更・契約解除）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．甲は、次の各号に該当する場合には、乙への事前通知を行うことなく本件サービスの提供を停止または中断することができる。</Paragraph>
        <ul style={{ paddingLeft: 24, margin: 0 }}>
          <li>（１）サーバー保守、点検、更新等を行う場合</li>
          <li>（２）災害、停電、通信障害、法令改正、外部サービス障害、クラッキング、不正アクセス等により正常な運営ができなくなった場合</li>
          <li>（３）その他、甲が提供の継続を困難と判断した場合</li>
        </ul>
        <Paragraph>２．甲は、前項に基づく停止又は変更によって乙に損害が生じた場合でも、これに対する何らの責任を負わないものとする。</Paragraph>
        <Paragraph>３．乙が本契約に違反し、甲が相当期間を定めて是正を求めたにもかかわらず是正されない場合、又は乙が反社会的勢力に該当すると判明した場合、その他、甲が乙による本件サービスの利用継続が適当ではないと判断した場合、甲は催告を要せず、乙の登録を抹消し、本契約を解除することができる。この場合、乙は甲に対して負うすべての債務について当然に期限の利益を失い、これらを一括にて支払わなければならない。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第１１条（損害賠償及び免責）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．甲は、本件サービスが、乙の目的に適合すること、乙が期待する機能、有用性、品質を有すること、乙に提供する解析結果、数値、その他の出力内容の真実性、正確性、完全性、エラー、バグその他の不具合の不存在、セキュリティ上の欠陥の不存在について、いかなる保証も行わないものとする。</Paragraph>
        <Paragraph>２．本件サービスの利用に関連して乙に損害が生じた場合、甲が負う損害賠償の総額は、当該損害発生日を含む月における乙の利用料金相当額を上限とする。</Paragraph>
        <Paragraph>３．甲は、本件サービスの利用に関し、天災地変、戦争、テロ、通信障害、外部サービス障害その他不可抗力、クラッキングその他のサイバー攻撃により乙に生じた損害について、一切の責任を負わない。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第１２条（反社会的勢力の排除）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．甲及び乙は、自ら又はその役員・従業員が暴力団、暴力団関係企業、総会屋、社会運動標ぼうゴロ、特殊知能暴力集団その他これらに準ずる反社会的勢力に該当せず、またこれらと資金提供、取引、便宜供与その他の関係を有しないことを表明し、保証する。</Paragraph>
        <Paragraph>２．前項に違反した場合、相手方は催告を要せず直ちに契約を解除することができる。</Paragraph>
      </Space>
    ),
  },
  {
    heading: "第１３条（準拠法及び裁判管轄）",
    body: (
      <Paragraph>
        本契約の準拠法は日本法とし、本契約に関して紛争が生じた場合、京都地方裁判所を第一審の専属的合意管轄裁判所とする。
      </Paragraph>
    ),
  },
  {
    heading: "第１４条（契約内容の変更）",
    body: (
      <Space direction="vertical" size="small">
        <Paragraph>１．甲は、必要に応じて本契約の内容を変更することができる。</Paragraph>
        <Paragraph>２．変更後の内容は、甲が本件サービス上または当社Webサイトに掲示し、かつ乙の登録メールアドレス宛に発信した日から３０日を経過した日をもって効力を生じるものとする。</Paragraph>
        <Paragraph>３．乙が変更通知後３０日以内に異議を述べない場合、乙は改定後の条件に同意したものとみなす。但し、法令上、乙の同意が必要である場合はこの限りではない。</Paragraph>
      </Space>
    ),
  },
];

export const TermsPrivacyPage: FC = () => {
  const termsAnchorItems = termsSections.map((section, index) => ({
    key: `term-${index}`,
    href: `#term-${index}`,
    title: section.heading,
  }));

  const anchorItems = [
    { key: 'terms', title: '利用規約', href: '#terms', children: termsAnchorItems },
    { key: 'privacy', title: '個人情報保護方針および個人情報の取り扱いについて', href: privacyPolicyUrl, target: '_blank' as const },
  ];

  const getCardProps = (heading: string) => {
    if (includesArticleNumber(heading, 3) || heading.includes("利用料金") || heading.includes("料金")) {
      return {
        style: { borderColor: '#faad14', borderWidth: 2 },
        title: (
          <Space>
            <DollarOutlined style={{ color: '#faad14' }} />
            <span>{heading}</span>
          </Space>
        ),
      };
    }
    if (includesArticleNumber(heading, 10) || heading.includes("契約解除")) {
      return {
        style: { borderColor: '#ff4d4f', borderWidth: 2 },
        title: (
          <Space>
            <LogoutOutlined style={{ color: '#ff4d4f' }} />
            <span>{heading}</span>
          </Space>
        ),
      };
    }
    if (includesArticleNumber(heading, 11) || heading.includes("免責")) {
      return {
        style: { borderColor: '#1677ff', borderWidth: 2 },
        title: (
          <Space>
            <SafetyOutlined style={{ color: '#1677ff' }} />
            <span>{heading}</span>
          </Space>
        ),
      };
    }
    return { title: heading, variant: "outlined" as const };
  };

  return (
    <>
      <SEOHead
        title="利用規約・プライバシーポリシー｜一元帳"
        description="一元帳の利用規約およびプライバシーポリシーをご確認いただけます。"
      />
      <PublicPageLayout title="利用規約" subtitle="AVSサービスをご利用いただく際の契約条件を掲載しています。">
      <Space direction="vertical" size={48} style={{ width: "100%" }}>
        <Alert
          message="本サービスは法人向けサブスクリプション（登録制）です。契約内容は最新情報を基に更新されます。"
          type="info"
          showIcon
        />

        <Row gutter={24}>
          <Col xs={0} md={6}>
            <Anchor
              targetOffset={80}
              items={anchorItems}
            />
          </Col>
          <Col xs={24} md={18}>
            <Space direction="vertical" size={48} style={{ width: "100%" }}>
              <section id="terms">
                <Title level={2}>利用規約</Title>
                <Space direction="vertical" size={32} style={{ width: "100%" }}>
                  {termsSections.map((section, index) => (
                    <Card key={section.heading} id={`term-${index}`} {...getCardProps(section.heading)}>
                      {section.body}
                    </Card>
                  ))}
                </Space>
              </section>

              <Card>
                <Space direction="vertical" size="small">
                  <Title level={3} style={{ marginBottom: 0 }}>
                    個人情報保護方針および個人情報の取り扱いについて
                  </Title>
                  <Paragraph style={{ marginBottom: 0 }}>
                    個人情報の取り扱い方針は
                    {' '}
                    <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                      こちらの個人情報保護方針および個人情報の取り扱いについて（外部サイト）
                    </a>
                    をご覧ください。
                  </Paragraph>
                </Space>
              </Card>

              <Paragraph type="secondary" style={{ marginTop: 24 }}>
                改定や重要なお知らせは本ページおよびダッシュボード内のお知らせで告知します。
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Space>
    </PublicPageLayout>
    </>
  );
};

export default TermsPrivacyPage;
